// Lightweight inline-SVG charts for the PUBLIC end-user report (/r/:token).
// Deliberately NOT Plotly: these pages are opened from an SMS link on a phone
// over cellular, so they must load instantly — no 1MB chart chunk. A donut is a
// few <circle> arcs; that is all a personal snapshot needs.

import { brand } from "../brand";

const PALETTE = brand.charts.categorical;
const EMPTY_RING = "#e8e8e8";

export interface MiniSlice {
  label: string;
  value: number;
  color?: string;
}

/** Assign a stable categorical color to any slice that didn't specify one. */
export function withColors(slices: MiniSlice[]): Required<MiniSlice>[] {
  return slices.map((s, i) => ({ ...s, color: s.color ?? PALETTE[i % PALETTE.length] }));
}

function fmt(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return (Number.isInteger(rounded) ? rounded : Number(rounded.toFixed(1))).toLocaleString();
}

/** Value + unit, currency-aware: "$" is a prefix ($120), everything else a suffix (12 hrs). */
export function formatValue(n: number, unit?: string): string {
  if (unit === "$") return `$${fmt(n)}`;
  return unit ? `${fmt(n)} ${unit}` : fmt(n);
}

export interface MiniDonutProps {
  slices: MiniSlice[];
  /** Unit appended to legend values (e.g. "hrs"). */
  unit?: string;
  size?: number;
  /** Big number drawn in the hole (e.g. the total). */
  centerTop?: string;
  centerSub?: string;
  emptyText?: string;
}

/**
 * Donut + legend. Slices are drawn as stroked arcs on a single ring with a small
 * gap between them (the 2px surface-gap idea, adapted to SVG). The legend gives
 * every slice a label + value + share, so identity never rides on color alone.
 */
export function MiniDonut({
  slices,
  unit = "",
  size = 190,
  centerTop,
  centerSub,
  emptyText = "No activity yet",
}: MiniDonutProps) {
  const data = withColors(slices).filter((s) => s.value > 0);
  const total = data.reduce((a, s) => a + s.value, 0);
  const stroke = Math.round(size * 0.16);
  const radius = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * radius;
  const gap = data.length > 1 ? 3 : 0; // px of ring left blank between slices

  let offset = 0;
  const arcs = data.map((s, i) => {
    const len = Math.max((s.value / total) * circ - gap, 0.5);
    const arc = (
      <circle
        key={i}
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke={s.color}
        strokeWidth={stroke}
        strokeDasharray={`${len} ${circ - len}`}
        strokeDashoffset={-offset}
        strokeLinecap="butt"
      />
    );
    offset += (s.value / total) * circ;
    return arc;
  });

  return (
    <div className="sn-mini">
      <svg
        className="sn-mini-svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={centerTop ? `${centerTop} ${centerSub ?? ""}` : "chart"}
      >
        <g transform={`rotate(-90 ${cx} ${cy})`}>
          <circle cx={cx} cy={cy} r={radius} fill="none" stroke={EMPTY_RING} strokeWidth={stroke} />
          {total > 0 && arcs}
        </g>
        {centerTop && (
          <text x={cx} y={cy} className="sn-mini-center-top" textAnchor="middle" dominantBaseline="middle">
            {centerTop}
          </text>
        )}
        {centerSub && (
          <text x={cx} y={cy + stroke * 0.9} className="sn-mini-center-sub" textAnchor="middle" dominantBaseline="middle">
            {centerSub}
          </text>
        )}
      </svg>
      {total === 0 ? (
        <p className="sn-mini-empty">{emptyText}</p>
      ) : (
        <ul className="sn-mini-legend">
          {data.map((s, i) => (
            <li key={i}>
              <span className="sn-mini-swatch" style={{ background: s.color }} />
              <span className="sn-mini-legend-label">{s.label}</span>
              <span className="sn-mini-legend-val">
                {formatValue(s.value, unit)} · {Math.round((s.value / total) * 100)}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
